import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { BarChart } from "@/shared/ui/BarChart";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { DownloadIcon, PackageIcon, ShoppingCartIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import {
  usePackageSalesByTemplateQuery,
  usePackageSalesSummaryQuery,
} from "../api/queries";
import {
  PackageSalesReportFilter,
  type PackageSalesReportSearch,
} from "../ui/PackageSalesReportFilter";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "../ui/ReportSummaryStatCard";
import { PackageSalesReportTable } from "../ui/PackageSalesReportTable";

function useReportQuery(search: PackageSalesReportSearch) {
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateFrom = search.dateFrom ?? defaultFrom;
  const dateTo = search.dateTo ?? defaultTo;
  const packageType = search.packageType ?? "all";

  const baseQuery = useMemo(
    () => ({
      dateFrom,
      dateTo,
      packageType,
      classTypeId: search.classTypeId,
      levelId: search.levelId,
    }),
    [dateFrom, dateTo, packageType, search.classTypeId, search.levelId],
  );

  const byTemplateQuery = useMemo(
    () => ({
      ...baseQuery,
      sortBy: search.sortBy ?? "count",
    }),
    [baseQuery, search.sortBy],
  );

  return { dateFrom, dateTo, baseQuery, byTemplateQuery };
}

function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function PackageSalesReportPage() {
  const search = useSearch({
    from: "/app/reports/package-sales",
  }) as PackageSalesReportSearch;

  const { dateFrom, dateTo, baseQuery, byTemplateQuery } =
    useReportQuery(search);

  const { data: summary, isLoading: summaryLoading } =
    usePackageSalesSummaryQuery(baseQuery);
  const { data: byTemplate = [], isLoading: byTemplateLoading } =
    usePackageSalesByTemplateQuery(byTemplateQuery);

  const exportCsv = useCallback(() => {
    const header = "ຊື່ແພັກເກດ,ປະເພດ,ຈຳນວນທີ່ຂາຍ,ຍອດຂາຍ,ປະເພດຄລາດສ໌/ລະດັບ";
    const rows = byTemplate.map(
      (r) =>
        `${escapeCsvCell(r.templateName)},${escapeCsvCell(r.packageType === "package" ? "ແພັກເກດຄລາດສ໌" : "ແພັກເກດຮຽນສ່ວນຕົວ")},${escapeCsvCell(r.salesCount)},${escapeCsvCell(r.revenue)},${escapeCsvCell((r.classTypes?.join("; ") ?? "") + (r.levels?.length ? " / " + (r.levels?.join("; ") ?? "") : ""))}`,
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `package-sales-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [byTemplate, dateFrom, dateTo]);

  const barChartData = useMemo(
    () =>
      byTemplate.slice(0, 10).map((r) => ({
        template:
          r.templateName.length > 20
            ? `${r.templateName.slice(0, 18)}...`
            : r.templateName,
        salesCount: r.salesCount,
      })),
    [byTemplate],
  );

  const barChartConfig: ChartConfig = {
    salesCount: { label: "ຈຳນວນທີ່ຂາຍ", color: "var(--chart-1)" },
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລາຍງານຍອດຂາຍແພັກເກດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານຍອດຂາຍແພັກເກດ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ສະແດງຈຳນວນທີ່ຂາຍແລະຍອດຂາຍແຍກຕາມແພັກເກດ (ກັ່ນຕາມວັນທີຈ່າຍ)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={byTemplate.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PackageSalesReportFilter />
        </div>

        <ReportSummaryStatGrid columns={2}>
          <ReportSummaryStatCard
            title="ຈຳນວນທີ່ຂາຍທັງໝົດ"
            icon={ShoppingCartIcon}
            tone="packages"
            isLoading={summaryLoading}
            value={reportSummaryCount(summary?.totalSalesCount ?? 0)}
          />
          <ReportSummaryStatCard
            title="ຍອດຂາຍທັງໝົດ"
            icon={PackageIcon}
            tone="revenue"
            isLoading={summaryLoading}
            amountKip={summary?.totalRevenue ?? 0}
          />
        </ReportSummaryStatGrid>

        {barChartData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>ແພັກເກດທີ່ຂາຍດີ (Top 10)</CardTitle>
              <p className="text-muted-foreground text-sm">
                ຈຳນວນທີ່ຂາຍແລະຍອດຂາຍແຕ່ລະແພັກເກດ
              </p>
            </CardHeader>
            <CardContent>
              <BarChart
                data={barChartData}
                config={barChartConfig}
                xDataKey="template"
                valueDataKey="salesCount"
                xAxisTickFormatter={(v) =>
                  typeof v === "string" && v.length > 15
                    ? `${v.slice(0, 12)}...`
                    : String(v)
                }
                className="mx-auto aspect-video max-h-[320px]"
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງຍອດຂາຍແຍກຕາມແພັກເກດ</CardTitle>
            <p className="text-muted-foreground text-sm">
              ຈຳນວນທີ່ຂາຍ ຍອດຂາຍ ແລະປະເພດຄລາດສ໌/ລະດັບ
            </p>
          </CardHeader>
          <CardContent>
            <PackageSalesReportTable
              isLoading={byTemplateLoading}
              data={byTemplate}
            />
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
